<template>
  <div>
    <Popover class="h-full md:relative">
      <SearchInput
        v-model="query"
        id="global-search"
        ref="inputRef"
        class="h-full"
        @focus="onInputFocus"
      />

      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="translate-y-1 opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="translate-y-1 opacity-0"
      >
        <PopoverPanel
          v-if="showPopover"
          class="absolute left-0 z-10 w-full max-w-4xl md:-mt-2"
          static
        >
          <div
            class="screen-height relative flex flex-col bg-white shadow-lg ring-1 ring-black/5 md:flex-row md:rounded-md"
          >
            <button
              type="button"
              class="absolute right-0 top-0 mr-2 mt-2 inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-600 md:hidden"
              @click="hidePopover"
            >
              <span class="sr-only">Close search menu</span>
              <XMarkIcon class="block size-6" aria-hidden="true" />
            </button>

            <aside
              class="space-y-4 border-b border-gray-200 p-4 md:basis-64 md:border-b-0 md:border-r"
            >
              <div class="space-y-2">
                <Label class="!mb-0">
                  {{ $t('components.search-bar.search-for') }}
                </Label>
                <Checkbox
                  v-model="filters.searchForClients"
                  id="search-for-clients"
                  :label="$t('general.clients')"
                />
                <Checkbox
                  v-model="filters.searchForInventories"
                  id="search-for-inventories"
                  :label="$t('general.inventories')"
                />
                <Checkbox
                  v-model="filters.searchForTasks"
                  id="search-for-tasks"
                  :label="$t('general.tasks')"
                />
              </div>

              <TagsInput
                v-model="filters.searchInRollouts"
                ref="rolloutTagsInput"
                :label="$t('components.search-bar.search-in')"
                :placeholder="
                  $t('components.search-bar.rollout-selection-placeholder')
                "
                :options="availableRollouts"
              />
            </aside>

            <div
              class="grow overflow-auto bg-gray-50 md:max-h-96"
              :class="{
                'flex items-center justify-center':
                  isLoadingResults || hasEmptyResults,
              }"
            >
              <template v-if="!isLoadingResults">
                <template v-if="!hasEmptyResults">
                  <table class="min-w-full">
                    <tbody class="bg-white">
                      <template
                        v-for="(resultKey, resultKeyIdx) in filteredResultKeys"
                        :key="resultKey"
                      >
                        <tr class="sticky top-0 z-10">
                          <th
                            colspan="4"
                            scope="colgroup"
                            class="bg-gray-50 p-0 text-left text-xs font-semibold text-gray-600"
                          >
                            <span
                              class="block border-b py-2 pl-4 pr-3 ring-gray-200 sm:pl-3"
                            >
                              {{ $t('general.' + resultKey) }}
                            </span>
                          </th>
                        </tr>
                        <tr
                          v-for="(result, resultIdx) in results[
                            resultKey as keyof SearchResults
                          ]"
                          :key="result._id"
                          class="cursor-pointer focus-within:bg-indigo-50 hover:bg-indigo-50"
                          :class="{
                            'border-b':
                              resultKeyIdx < filteredResultKeys.length - 1 ||
                              resultIdx <
                              results[resultKey as keyof SearchResults]
                                .length -
                              1,
                          }"
                          @click="
                            onResultClick(
                              resultKey as keyof SearchResults,
                              result
                            )
                          "
                        >
                          <td
                            class="whitespace-nowrap py-4 pl-4 pr-3 text-[0] font-medium text-gray-900 sm:pl-3"
                          >
                            <ClientName
                              v-if="resultKey === 'clients'"
                              :client="result as Client"
                            />
                            <span v-else class="text-sm">
                              {{ result.name }}
                            </span>
                            <input
                              type="text"
                              class="pointer-events-none absolute size-0 select-none border-none p-0 focus:outline-none focus:ring-0"
                              readonly
                              aria-hidden="true"
                              @keyup.enter="
                                onResultClick(
                                  resultKey as keyof SearchResults,
                                  result
                                )
                              "
                            />
                          </td>
                          <td
                            class="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500"
                          >
                            <template v-if="resultKey === 'tasks'">
                              <Badge
                                v-if="(result as EditClientTask).date"
                                :color="
                                  getDueDateBadgeColor(result as EditClientTask)
                                "
                              >
                                <Date
                                  v-if="(result as EditClientTask).date"
                                  :date="(result as EditClientTask).date!"
                                  :show-time="
                                    !(result as EditClientTask).isAllDayTask
                                  "
                                />
                              </Badge>
                              <template v-else> - </template>
                            </template>
                          </td>
                          <td
                            class="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500"
                          >
                            <template v-if="resultKey === 'tasks'">
                              <Badge
                                :title="
                                  (result as EditClientTask).done
                                    ? $t('general.done')
                                    : $t('general.todo')
                                "
                                :color="
                                  (result as EditClientTask).done
                                    ? 'green'
                                    : 'gray'
                                "
                                pill
                              />
                            </template>
                            <template v-else-if="resultKey === 'clients'">
                              <ClientStatusBadge :client="result as Client" />
                            </template>
                          </td>
                          <td
                            class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm sm:pr-3"
                          >
                            <template
                              v-if="['tasks', 'clients'].includes(resultKey)"
                            >
                              <UserAvatar
                                v-if="(result as EditClientTask).assignee"
                                :user="
                                  (result as EditClientTask).assignee as User
                                "
                                size="6"
                                :has-popover="false"
                              />
                              <span v-else class="inline-block w-6 text-center">
                                -
                              </span>
                            </template>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </template>
                <EmptyState
                  v-else
                  :title="$t('components.search-bar.empty-state')"
                  :subtitle="$t('components.search-bar.empty-state-subtitle')"
                  :icon="MagnifyingGlassIcon"
                  margin-top="mt-0"
                />
              </template>
              <Spinner v-else size="10" colored />
            </div>
          </div>
        </PopoverPanel>
      </transition>
    </Popover>

    <ClientTaskSlideOver
      v-model="isTaskSlideOverVisible"
      :client-task-id="clientTaskToEditId"
      show-open-client-button
    />
  </div>
</template>

<script lang="ts">
export type GlobalSearchResponse = {
  clientTask: EditClientTask[];
  inventory: Inventory[];
  client: Client[];
};
</script>

<script lang="ts" setup>
import { Popover, PopoverPanel } from '@headlessui/vue';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/24/solid';
import { debounce } from 'lodash';
import { useOutsideClick } from '@/util/use-outside-click';
import { DATABASE_AUTOCOMPLETE_COLLECTION } from '@/types';
import type { Rollout, EditClientTask, Client, Inventory, User } from '@/types';
import { useUserStore } from '@/stores/user';
import { getDueDateBadgeColor } from '@/util/client-task-utils';
import { TagsInput } from '#components';

const query = ref('');
const showPopover = ref(false);
const inputRef = ref();

const isTaskSlideOverVisible = ref(false);
const clientTaskToEditId = ref<string | undefined>();

type SearchResults = {
  clients: Client[];
  tasks: EditClientTask[];
  inventories: Inventory[];
};

const results = ref<SearchResults>({
  clients: [],
  tasks: [],
  inventories: [],
});

const filteredResultKeys = computed(() => {
  return Object.keys(results.value).filter(
    (key) => results.value[key as keyof SearchResults].length
  );
});

const hasEmptyResults = computed(() => {
  return Object.keys(results.value).every(
    (key) => !results.value[key as keyof SearchResults].length
  );
});

const resetResults = () => {
  Object.keys(results.value).forEach((key) => {
    results.value[key as keyof SearchResults] = [];
  });
};

const hidePopover = () => {
  showPopover.value = false;
  query.value = '';
  filters.value = getInitialFilters();
  resetResults();
};

const onResultClick = (
  resultKey: keyof SearchResults,
  element: Client | Inventory | EditClientTask
) => {
  switch (resultKey) {
    case 'clients':
      navigateTo(
        `/rollout-project/${
          ((element as Client).rollout as unknown as Rollout).identifier
        }/inventory/clients/view/${element._id}`
      );
      break;
    case 'inventories':
      navigateTo(
        `/rollout-project/${
          ((element as Inventory).rollout as unknown as Rollout).identifier
        }/inventory/inventories/dashboard/${element._id}`
      );
      break;
    case 'tasks':
      editClientTask(element as EditClientTask);
  }

  hidePopover();
};

const rolloutTagsInput = ref<InstanceType<typeof TagsInput> | null>(null);
const onOutsideClick = (event: Event) => {
  let isChildOfCombobox = false;
  let currentNode = event.target;

  // checks if clicked target is contained by rollout tags input combobox options which are not part of the HTML Element, but teleported to body
  const comboboxOptionsElement =
    rolloutTagsInput.value?.comboboxOptionsRef?.$el;
  if (
    comboboxOptionsElement &&
    comboboxOptionsElement.contains(currentNode as HTMLElement)
  ) {
    return;
  }

  while (currentNode !== null) {
    const id = (currentNode as HTMLElement).id;
    if (
      id?.startsWith('headlessui-popover-panel') ||
      id?.startsWith('global-search')
    ) {
      isChildOfCombobox = true;
      break;
    }
    currentNode = (currentNode as HTMLElement).parentNode;
  }

  if (!isChildOfCombobox) {
    hidePopover();
  }
};

useOutsideClick(
  [inputRef.value?.$el],
  onOutsideClick,
  computed(() => showPopover.value),
  { usePointerDown: true }
);

const onInputFocus = () => {
  if (!showPopover.value) {
    filters.value = getInitialFilters();
  }
  showPopover.value = true;
};

const userStore = useUserStore();
const availableRollouts = computed(() => userStore.availableRollouts);
const { selectedRollout } = useRolloutRouting();
const { $client } = useNuxtApp();

// Searching

type SearchFilters = {
  searchForClients: boolean;
  searchForInventories: boolean;
  searchForTasks: boolean;
  searchInRollouts: Rollout[];
};

const getInitialFilters = (): SearchFilters => {
  const searchInRollouts: Rollout[] = [];

  if (selectedRollout.value) {
    searchInRollouts.push(selectedRollout.value);
  }

  return {
    searchForClients: true,
    searchForInventories: true,
    searchForTasks: true,
    searchInRollouts,
  };
};

const filters = ref<SearchFilters>(getInitialFilters());

const searchCollections = computed<DATABASE_AUTOCOMPLETE_COLLECTION[]>(() => {
  if (
    !filters.value.searchForClients &&
    !filters.value.searchForInventories &&
    !filters.value.searchForTasks
  ) {
    return [
      DATABASE_AUTOCOMPLETE_COLLECTION.Client,
      DATABASE_AUTOCOMPLETE_COLLECTION.Inventory,
      DATABASE_AUTOCOMPLETE_COLLECTION.ClientTask,
    ];
  } else {
    return [
      filters.value.searchForClients
        ? DATABASE_AUTOCOMPLETE_COLLECTION.Client
        : null,
      filters.value.searchForInventories
        ? DATABASE_AUTOCOMPLETE_COLLECTION.Inventory
        : null,
      filters.value.searchForTasks
        ? DATABASE_AUTOCOMPLETE_COLLECTION.ClientTask
        : null,
    ].filter(Boolean) as DATABASE_AUTOCOMPLETE_COLLECTION[];
  }
});

watch(searchCollections, () => {
  if (query.value) {
    fetchSearch();
  }
});

const searchRollouts = computed(() => {
  return !filters.value.searchInRollouts.length
    ? availableRollouts.value
    : filters.value.searchInRollouts;
});

watch(searchRollouts, () => {
  if (query.value) {
    fetchSearch();
  }
});

const isLoadingResults = ref(false);
const i18n = useI18n();

const fetchSearch = debounce(() => {
  isLoadingResults.value = true;

  $client.search.search
    .query({
      rolloutIds: searchRollouts.value.map((rollout) => rollout._id),
      collections: searchCollections.value,
      q: query.value,
    })
    .then((res: GlobalSearchResponse) => {
      results.value.tasks = res.clientTask?.length ? res.clientTask : [];
      results.value.clients = res.client ?? [];
      results.value.inventories = res.inventory ?? [];
    })
    .catch((err) => {
      useErrorHandler(err, {
        type: 'error',
        title: i18n.t('error-messages.something-went-wrong'),
        message: i18n.t('error-messages.please-contact-key-account-manager'),
      });
    })
    .finally(() => {
      isLoadingResults.value = false;
    });
}, 300);

const editClientTask = (clientTask: EditClientTask) => {
  clientTaskToEditId.value = clientTask._id;
  isTaskSlideOverVisible.value = true;
};

watch(query, (newValue) => {
  if (newValue) {
    fetchSearch();
  } else {
    resetResults();
  }
});
</script>

<style scoped>
@media all and (max-width: 768px) {
  .screen-height {
    @apply overflow-y-auto;
    height: calc(100vh - 64px);
  }
}
</style>
